import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Archive, FolderPlus, FolderOpen, X, RefreshCw, 
    ShieldCheck, AlertTriangle, RotateCcw, Search, 
    CheckSquare, Square, FileText, Users, BarChart3,
    Check
} from 'lucide-react';
import {
    setSurveyArchiveDirHandle,
    getActiveArchiveFileName,
    setActiveFileHandle, getActiveFileHandleRef,
    preflightWorkspaceArchive,
    restoreSelectiveArchive,
    createEmptyArchiveFile, archiveFileExists,
    inspectArchiveFile,
    ARCHIVE_FOREIGN
} from '../../utils/archiveApi';
import SamSpinner from '../SamSpinner';
import ConfirmModal from '../ConfirmModal';
import TextInputModal from '../TextInputModal';
import { showToast } from '../../utils/alerts-messages';
import { getFeatureFlag } from '../../sysconfig';

// Selective-restore UI is gated (default OFF) so restore stays hidden until the
// backend acceptance passes. The authoritative gate is the SERVER flag
// ARCHIVE_SELECTIVE_RESTORE_UI (surfaced via /config features, resolved at runtime
// below); REACT_APP_ARCHIVE_SELECTIVE_RESTORE_UI=true is a build-time dev override.
const SELECTIVE_RESTORE_UI_BUILD_FLAG = process.env.REACT_APP_ARCHIVE_SELECTIVE_RESTORE_UI === 'true';

/**
 * ArchiveManagerSection — The unified Accounts > Archives section.
 * Replaces AccountArchiveSection and SurveyArchiveSection.
 */
export default function ArchiveManagerSection({ onArchiveStateChange }) {
    const [fileHandle, setFileHandle] = useState(getActiveFileHandleRef());
    const [fileName, setFileName] = useState(getActiveArchiveFileName());
    const [health, setHealth] = useState({ status: 'unknown', reason: '', version: null });

    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [reloading, setReloading] = useState(false);

    // Modal-driven "New archive" flow (styled dialogs, not window.prompt/confirm).
    const [namePrompt, setNamePrompt] = useState(null);      // { dirHandle } while asking for a name
    const [overwritePrompt, setOverwritePrompt] = useState(null); // { dirHandle, name } while confirming overwrite

    // Two-phase restore preview: a preflight resolves the selection's closure
    // (roots + shared dependencies) and detects ID collisions WITHOUT mutating.
    // The operator reviews dependencies/collisions and then explicitly confirms.
    // Collisions are BLOCKING — there is no overwrite / import-as-new / re-home
    // path — so a preview with collisions offers no confirm button.
    // { report: SelectiveRestoreReport, collisions: [{ table, key, value }] }
    const [restorePreview, setRestorePreview] = useState(null);

    // Selective-restore UI gate resolved at runtime from the server flag
    // (ARCHIVE_SELECTIVE_RESTORE_UI via /config features), OR the build-time dev
    // override. Default closed until the server enables it.
    const [uiFlagEnabled, setUiFlagEnabled] = useState(SELECTIVE_RESTORE_UI_BUILD_FLAG);
    useEffect(() => {
        let cancelled = false;
        getFeatureFlag('archive_selective_restore_ui').then((on) => {
            if (!cancelled && on) setUiFlagEnabled(true);
        });
        return () => { cancelled = true; };
    }, []);

    // ─── Health Check ──────────────────────────────────────────────────
    // Works directly on the active file handle (Open selects the .samarchive
    // itself; New creates it and adopts its handle), rather than resolving a
    // filename inside a directory handle.
    const checkHealth = useCallback(async (handle) => {
        if (!handle) {
            setHealth({ status: 'none', reason: 'No archive file open' });
            setManifest(null);
            return;
        }
        try {
            const perm = await handle.queryPermission({ mode: 'readwrite' });
            if (perm !== 'granted') {
                setHealth({ status: 'stale', reason: 'Permission required' });
                return;
            }

            const file = await handle.getFile();

            // A valid, meaningful "Archived" date for the rows: the archive
            // file's own last-modified time. The preflight result the table is
            // built from carries no per-item archived timestamp and no top-level
            // created_at, which is why the column previously rendered "Invalid
            // Date". We supply this as the fallback the rows fall back to.
            const archivedAtISO = file.lastModified
                ? new Date(file.lastModified).toISOString()
                : new Date().toISOString();

            // Ownership gate: never expose an archive stamped with another
            // account's org id.
            const info = await inspectArchiveFile(file);
            if (info.ownership === ARCHIVE_FOREIGN) {
                setHealth({ status: 'error', reason: 'This archive belongs to another account' });
                setManifest(null);
                return;
            }

            if (info.empty) {
                // A brand-new (or freshly created, ownership-stamped) archive
                // holds no survey data yet — treat it as a clean, ready state
                // rather than sending it through server preflight, which requires
                // a data payload.
                setHealth({ status: 'empty', reason: 'Empty archive · ready to fill' });
                setManifest({
                    surveys: [], recipients: [], contents: [],
                    created_at: archivedAtISO
                });
            } else {
                const data = await preflightWorkspaceArchive(file);
                setHealth({
                    status: 'healthy',
                    reason: 'Reachable · Writable · Signature valid',
                    version: data.checksum?.slice(0, 8)
                });
                setManifest({ ...data, created_at: data.created_at || archivedAtISO });
            }
        } catch (err) {
            // Preflight reads the archive, so it is Professional+. A starter
            // account is refused here — say so, rather than blaming the file.
            if (err.data?.error === 'UPGRADE_REQUIRED') {
                setHealth({ status: 'locked', reason: err.message });
            } else {
                setHealth({ status: 'error', reason: 'Archive file unreachable' });
            }
        }
    }, []);

    useEffect(() => {
        checkHealth(fileHandle);
    }, [fileHandle, checkHealth]);

    // Report open/content state up to App so the survey list's Archive and Load
    // Archive buttons enable/disable correctly. An archive counts as "open" only
    // when it's usable for this account (healthy or empty).
    useEffect(() => {
        if (!onArchiveStateChange) return;
        const usable = health.status === 'healthy' || health.status === 'empty';
        const isOpen = usable && !!fileHandle;
        const hasContent = !!(manifest && (
            (manifest.surveys && manifest.surveys.length) ||
            (manifest.recipients && manifest.recipients.length) ||
            (manifest.contents && manifest.contents.length)
        ));
        onArchiveStateChange(isOpen, isOpen ? hasContent : false);
    }, [fileHandle, health.status, manifest, onArchiveStateChange]);

    // ─── File Actions ──────────────────────────────────────────────────
    // Normalize a user-typed name into a safe .samarchive filename.
    const normalizeArchiveName = (raw) => {
        let name = (raw || '').trim();
        if (!name) return '';
        // Strip path separators and characters that are illegal in filenames.
        name = name.replace(/[\\/:*?"<>|]/g, '_');
        if (!name.toLowerCase().endsWith('.samarchive')) name += '.samarchive';
        return name;
    };

    // Render a date safely — never show "Invalid Date".
    const formatDate = (value) => {
        if (!value) return '—';
        const d = new Date(value);
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
    };

    // New: creating a file needs a folder to place it in, so this step still uses
    // the directory picker — but only to create the file; we then adopt the new
    // file's own handle as the active archive.
    const handleNew = async () => {
        try {
            const dir = await window.showDirectoryPicker({ mode: 'readwrite' });
            setNamePrompt({ dirHandle: dir });
        } catch (err) {
            if (err.name !== 'AbortError') showToast(err.message, 'error');
        }
    };

    const handleNameSubmit = async (rawName) => {
        const dir = namePrompt?.dirHandle;
        setNamePrompt(null);
        if (!dir) return;
        const name = normalizeArchiveName(rawName);
        if (!name) {
            showToast('Please provide a valid archive name', 'error');
            return;
        }
        try {
            // createEmptyArchiveFile writes into the directory handle held by the
            // archive module, so set it transiently for this create.
            await setSurveyArchiveDirHandle(dir);
            if (await archiveFileExists(name)) {
                setOverwritePrompt({ dirHandle: dir, name });
                return;
            }
            await finalizeNewArchive(name);
        } catch (err) {
            if (err.name !== 'AbortError') showToast(err.message, 'error');
        }
    };

    const handleOverwriteConfirm = async () => {
        const { name } = overwritePrompt || {};
        setOverwritePrompt(null);
        if (!name) return;
        try {
            await finalizeNewArchive(name);
        } catch (err) {
            if (err.name !== 'AbortError') showToast(err.message, 'error');
        }
    };

    const finalizeNewArchive = async (name) => {
        const fh = await createEmptyArchiveFile(name);
        await setActiveFileHandle(fh);
        setFileHandle(fh);
        setFileName(name);
        showToast(`Created ${name}`, 'success');
        // Derive health/manifest immediately, the same way Refresh and Re-open
        // do. Leaning on the [fileHandle] effect alone leaves the pre-open
        // "No archive file open" state painted until a manual refresh.
        setHealth({ status: 'checking', reason: 'Checking archive…' });
        await checkHealth(fh);
    };

    // Open: select the .samarchive file directly. We request read-write up front
    // (archiving copies survey data into the file), then reject archives that
    // belong to another account.
    const handleOpen = async () => {
        try {
            const [fh] = await window.showOpenFilePicker({
                types: [{
                    description: 'SAM Surveyor Archive',
                    accept: { 'application/octet-stream': ['.samarchive'] },
                }],
                multiple: false,
                excludeAcceptAllOption: false,
            });
            if (!fh) return;

            if (fh.requestPermission) {
                const perm = await fh.requestPermission({ mode: 'readwrite' });
                if (perm !== 'granted') {
                    showToast('Write permission to the archive was denied', 'error');
                    return;
                }
            }

            const file = await fh.getFile();
            const info = await inspectArchiveFile(file);
            if (info.ownership === ARCHIVE_FOREIGN) {
                showToast('That archive belongs to another account', 'error');
                return;
            }

            await setActiveFileHandle(fh);
            setFileHandle(fh);
            setFileName(fh.name);
            showToast(`Opened ${fh.name}`, 'success');
            // Derive health/manifest immediately, the same way Refresh and
            // Re-open do. Leaning on the [fileHandle] effect alone leaves the
            // pre-open "No archive file open" state painted until a manual
            // refresh.
            setHealth({ status: 'checking', reason: 'Checking archive…' });
            await checkHealth(fh);
        } catch (err) {
            if (err.name !== 'AbortError') showToast(err.message, 'error');
        }
    };

    const handleClose = async () => {
        await setActiveFileHandle(null);
        setFileHandle(null);
        setManifest(null);
        setHealth({ status: 'none', reason: 'No archive file open' });
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await checkHealth(fileHandle);
        setRefreshing(false);
    };

    const handleReopen = async () => {
        try {
            // Re-grant on the file handle we already hold — a simple "Allow"
            // prompt. If we have no usable handle, fall back to picking the file.
            const handle = fileHandle || getActiveFileHandleRef();
            if (handle && handle.requestPermission) {
                const perm = await handle.requestPermission({ mode: 'readwrite' });
                if (perm === 'granted') {
                    await setActiveFileHandle(handle);
                    setFileHandle(handle);
                    await checkHealth(handle);
                    return;
                }
            }
            await handleOpen();
        } catch (err) {
            if (err.name !== 'AbortError') showToast(err.message, 'error');
        }
    };

    // ─── Search & Filtering ────────────────────────────────────────────
    // Collapse any duplicate rows for the same logical artifact to a single
    // most-recent entry. The server overwrites archived artifacts in place
    // (whole-instance, keyed by primary key), but a survey re-archived after
    // gaining versions — or whose id string-form drifts across a merge — can
    // leave the contents listing with more than one row for one artifact. The
    // Accounts section shows only the most recent version per artifact; browsing
    // version history is a separate feature (deferred by design).
    const collapseLatest = (items) => {
        if (!Array.isArray(items)) return items;
        const byKey = new Map();
        for (const item of items) {
            const raw = item?.id;
            const key = (raw == null ? '' : String(raw)).trim().toLowerCase();
            const prev = byKey.get(key);
            if (!prev) { byKey.set(key, item); continue; }
            const pv = Number(prev?.version || 0);
            const cv = Number(item?.version || 0);
            if (cv > pv) { byKey.set(key, item); continue; }
            if (cv === pv) {
                const pm = Date.parse(prev?.last_modified || prev?.archived_at || '') || 0;
                const cm = Date.parse(item?.last_modified || item?.archived_at || '') || 0;
                if (cm >= pm) byKey.set(key, item);
            }
        }
        return Array.from(byKey.values());
    };

    const filteredManifest = useMemo(() => {
        if (!manifest) return null;

        const base = {
            ...manifest,
            surveys: collapseLatest(manifest.surveys),
            recipients: collapseLatest(manifest.recipients),
            // Results are represented in contents usually
            contents: collapseLatest(manifest.contents),
        };

        const q = searchQuery.toLowerCase();
        if (!q) return base;

        const filter = (items) => (items || []).filter(item =>
            (item.name || '').toLowerCase().includes(q) ||
            (item.id || '').toLowerCase().includes(q) ||
            (item.kind || '').toLowerCase().includes(q) ||
            (item.slug || '').toLowerCase().includes(q)
        );

        return {
            ...base,
            surveys: filter(base.surveys),
            recipients: filter(base.recipients),
            contents: filter(base.contents),
        };
    }, [manifest, searchQuery]);

    // Rows the Results group draws. Deployments are now selectable roots (a
    // deployment restores its results graph), so their ids join allVisibleIds and
    // participate in select-all and the "(N)" count.
    const visibleDeployments = useMemo(
        () => (filteredManifest?.contents || []).filter(c => c.kind === 'deployment'),
        [filteredManifest]
    );

    const allVisibleIds = useMemo(() => {
        if (!filteredManifest) return [];
        const ids = [];
        (filteredManifest.surveys || []).forEach(s => ids.push(s.id));
        (filteredManifest.recipients || []).forEach(r => ids.push(r.id));
        (filteredManifest.contents || []).forEach(c => {
            if (c.kind === 'deployment') ids.push(c.id);
        });
        return ids;
    }, [filteredManifest]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === allVisibleIds.length && allVisibleIds.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(allVisibleIds));
        }
    };

    // ─── Reload (restore) ────────────────────────────────────────────────
    // Restore never modifies the archive file (design §9.7). buildSelector
    // classifies the selected rows into the current-format selector (survey /
    // deployment / recipient roots). Deployments are the `deployment` entries in
    // manifest.contents; surveys and recipients are their own lists.
    const buildSelector = () => {
        const survey_ids = [];
        const deployment_ids = [];
        const recipient_ids = [];
        (filteredManifest?.surveys || []).forEach(s => { if (selectedIds.has(s.id)) survey_ids.push(s.id); });
        (filteredManifest?.recipients || []).forEach(r => { if (selectedIds.has(r.id)) recipient_ids.push(r.id); });
        (filteredManifest?.contents || []).forEach(c => {
            if (c.kind === 'deployment' && selectedIds.has(c.id)) deployment_ids.push(c.id);
        });
        return { survey_ids, deployment_ids, recipient_ids };
    };

    // Phase 1 — PREFLIGHT: resolve the closure (roots + shared dependencies) and
    // detect ID collisions WITHOUT mutating, then open the review modal. There is
    // NO overwrite or import-as-new path — a colliding ID blocks the restore.
    const handleReload = async () => {
        if (selectedIds.size === 0 || !fileHandle) return;
        setReloading(true);
        try {
            const file = await fileHandle.getFile();
            const report = await restoreSelectiveArchive(file, buildSelector(), { preflight: true });
            setRestorePreview({ report, collisions: report.collisions || [] });
        } catch (err) {
            if (err.status === 409 && err.data) {
                setRestorePreview({ report: err.data, collisions: err.data.collisions || [] });
            } else if (err.status === 422) {
                showToast(`Restore refused: ${err.message}`, 'error');
            } else {
                showToast(`Preflight failed: ${err.message}`, 'error');
            }
        } finally {
            setReloading(false);
        }
    };

    // Phase 2 — EXPLICIT CONFIRM: apply the restore for real (no preflight flag).
    const confirmRestore = async () => {
        if (!fileHandle) return;
        setReloading(true);
        try {
            const file = await fileHandle.getFile();
            const result = await restoreSelectiveArchive(file, buildSelector());
            const inserted = result?.inserted_counts
                ? Object.values(result.inserted_counts).reduce((a, b) => a + b, 0)
                : 0;
            showToast(`Restore complete${inserted ? ` — ${inserted} row${inserted === 1 ? '' : 's'} restored` : ''}.`, 'success');
            setSelectedIds(new Set());
            setRestorePreview(null);
            checkHealth(fileHandle);
        } catch (err) {
            if (err.status === 409 && err.data) {
                setRestorePreview({ report: err.data, collisions: err.data.collisions || [] });
            } else if (err.status === 422) {
                showToast(`Restore refused: ${err.message}`, 'error');
            } else {
                showToast(`Restore failed: ${err.message}`, 'error');
            }
        } finally {
            setReloading(false);
        }
    };

    // ─── Render ────────────────────────────────────────────────────────
    return (
        <div style={S.container}>
            <div style={S.header}>
                <h2 style={S.title}>
                    <Archive size={22} style={S.titleIcon} />
                    Archive Manager
                </h2>
                <p style={S.subtitle}>
                    Unified management for your portable workspace archives.
                </p>
            </div>

            {/* Card 1: Archive File */}
            <div style={S.card}>
                <div style={S.cardHeader}>
                    <h3 style={S.cardTitle}>Archive File</h3>
                    <div style={S.fileActions}>
                        <button onClick={handleNew} style={S.actionBtn} title="New Archive">
                            <FolderPlus size={16} style={S.btnIcon} /> New
                        </button>
                        <button onClick={handleOpen} style={S.actionBtn} title="Open Archive">
                            <FolderOpen size={16} style={S.btnIcon} /> Open
                        </button>
                        {fileHandle && (
                            <button onClick={handleClose} style={S.actionBtn} title="Close Archive">
                                <X size={16} style={S.btnIcon} /> Close
                            </button>
                        )}
                    </div>
                </div>

                {fileHandle ? (
                    <div style={S.fileInfo}>
                        <div style={S.activeFileLine}>
                            <Check size={14} style={S.checkIcon} />
                            <span style={S.fileName}>{fileName}</span>
                        </div>
                        
                        <div style={S.healthRow}>
                            {(() => {
                                // 'healthy' and 'empty' are both benign (green);
                                // 'checking' is a transient neutral state while the
                                // just-opened file is being verified; everything
                                // else is an actual problem (red).
                                const benign = health.status === 'healthy' || health.status === 'empty';
                                const checking = health.status === 'checking';
                                const color = benign ? '#86EFAC' : checking ? '#94A3B8' : '#FDA4AF';
                                const tint = benign ? '34,197,94' : checking ? '148,163,184' : '244,63,94';
                                return (
                                    <div style={{
                                        ...S.healthStrip,
                                        color,
                                        background: `rgba(${tint},0.1)`,
                                        border: `1px solid rgba(${tint},0.2)`
                                    }}>
                                        {benign ? <ShieldCheck size={14} />
                                            : checking ? <RefreshCw size={14} className="spin" />
                                            : <AlertTriangle size={14} />}
                                        <span style={S.healthText}>
                                            {health.reason} {health.version && `· v${health.version}`}
                                        </span>
                                    </div>
                                );
                            })()}

                            {health.status !== 'healthy' && health.status !== 'empty' && health.status !== 'none' && health.status !== 'checking' && (
                                <button onClick={handleReopen} style={S.reopenBtn}>Re-open</button>
                            )}
                            
                            <button onClick={handleRefresh} disabled={refreshing} style={S.refreshBtn}>
                                <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={S.emptyState}>
                        <p style={S.emptyText}>Open or create an archive file to see its contents.</p>
                    </div>
                )}
            </div>

            {/* Card 2: Archive Contents */}
            <div style={S.card}>
                <div style={S.cardHeader}>
                    <h3 style={S.cardTitle}>Archive Contents</h3>
                    {manifest && (
                        <div style={S.contentsActions}>
                            <div style={S.searchWrapper}>
                                <Search size={14} style={S.searchIcon} />
                                <input 
                                    type="text" 
                                    placeholder="Search this archive..." 
                                    style={S.searchInput}
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && <X size={14} style={S.searchClear} onClick={() => setSearchQuery('')} />}
                            </div>
                            
                            {uiFlagEnabled && (
                                <button
                                    onClick={handleReload}
                                    disabled={selectedIds.size === 0 || reloading}
                                    style={{
                                        ...S.reloadBtn,
                                        opacity: selectedIds.size === 0 || reloading ? 0.5 : 1
                                    }}
                                >
                                    <RotateCcw size={16} style={S.btnIcon} />
                                    {reloading ? 'Reloading...' : `Reload Selected (${selectedIds.size})`}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Data volume summary. The rows above list which surveys,
                    recipients and deployments are archived, but not how much
                    result data came with them. A deployment can archive with
                    zero responses (no completed sessions), which otherwise looks
                    identical to a full one. These counts, read from the archive
                    by preflight, make "present but empty" visible. */}
                {manifest && manifest.data_counts && (
                    <div style={S.dataSummary}>
                        {(() => {
                            const dc = manifest.data_counts || {};
                            const results = Number(dc.results || 0);
                            const responses = Number(dc.responses || 0);
                            const sessions = Number(dc.sessions || 0);
                            const empty = results === 0 && responses === 0 && sessions === 0;
                            return (
                                <span style={empty ? S.dataSummaryEmpty : undefined}>
                                    {empty
                                        ? 'No result data in this archive — 0 results, 0 responses, 0 sessions.'
                                        : `Result data: ${results} result${results === 1 ? '' : 's'} · ${responses} response${responses === 1 ? '' : 's'} · ${sessions} session${sessions === 1 ? '' : 's'}.`}
                                </span>
                            );
                        })()}
                    </div>
                )}

                {!fileHandle ? (
                    <div style={S.emptyState}>
                        <p style={S.emptyText}>No archive file open.</p>
                    </div>
                ) : !manifest ? (
                    <div style={S.emptyState}>
                        {health.status === 'healthy' ? (
                            <SamSpinner size={40} text="Loading contents..." />
                        ) : (
                            <p style={S.emptyText}>{health.reason}</p>
                        )}
                    </div>
                ) : (
                    <div style={S.contentsTable}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>
                                        <div onClick={toggleSelectAll} style={S.checkbox}>
                                            {selectedIds.size === allVisibleIds.length && allVisibleIds.length > 0 ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </div>
                                    </th>
                                    <th style={S.th}>Name</th>
                                    <th style={S.th}>Type</th>
                                    <th style={S.th}>UUID</th>
                                    <th style={S.th}>Archived</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Group: Surveys */}
                                {filteredManifest.surveys?.length > 0 && (
                                    <>
                                        <tr style={S.groupHeader}><td colSpan="5">Surveys</td></tr>
                                        {filteredManifest.surveys.map(s => (
                                            <tr key={s.id} style={S.row}>
                                                <td style={S.td}>
                                                    <div onClick={() => toggleSelect(s.id)} style={S.checkbox}>
                                                        {selectedIds.has(s.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                                    </div>
                                                </td>
                                                <td style={S.td}><FileText size={14} style={S.typeIcon} /> {s.name}</td>
                                                <td style={S.td}><span style={S.chip}>Survey</span></td>
                                                <td style={S.td}><code style={S.mono}>{s.id.slice(0, 8)}...</code></td>
                                                <td style={S.td}>{formatDate(s.archived_at || manifest.created_at)}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}

                                {/* Group: Recipients */}
                                {filteredManifest.recipients?.length > 0 && (
                                    <>
                                        <tr style={S.groupHeader}><td colSpan="5">Recipients</td></tr>
                                        {filteredManifest.recipients.map(r => (
                                            <tr key={r.id} style={S.row}>
                                                <td style={S.td}>
                                                    <div onClick={() => toggleSelect(r.id)} style={S.checkbox}>
                                                        {selectedIds.has(r.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                                    </div>
                                                </td>
                                                <td style={S.td}><Users size={14} style={S.typeIcon} /> {r.name || r.email}</td>
                                                <td style={S.td}><span style={S.chip}>Recipient</span></td>
                                                <td style={S.td}><code style={S.mono}>{r.id.slice(0, 8)}...</code></td>
                                                <td style={S.td}>{formatDate(r.archived_at || manifest.created_at)}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}

                                {/* Group: Results. One row per archived deployment.
                                    Deployments are selectable roots: restoring one pulls
                                    its results graph (sessions/responses/etc.). */}
                                {visibleDeployments.length > 0 && (
                                    <>
                                        <tr style={S.groupHeader}><td colSpan="5">Results</td></tr>
                                        {visibleDeployments.map(d => (
                                            <tr key={d.id} style={S.row}>
                                                <td style={S.td}>
                                                    <div onClick={() => toggleSelect(d.id)} style={S.checkbox}>
                                                        {selectedIds.has(d.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                                    </div>
                                                </td>
                                                <td style={S.td}>
                                                    <BarChart3 size={14} style={S.typeIcon} /> {d.name}
                                                    <span style={S.rowDetail}>
                                                        {d.sessions} session{d.sessions === 1 ? '' : 's'} · {d.responses} response{d.responses === 1 ? '' : 's'}
                                                    </span>
                                                </td>
                                                <td style={S.td}><span style={S.chip}>Results</span></td>
                                                <td style={S.td}><code style={S.mono}>{d.id.slice(0, 8)}...</code></td>
                                                <td style={S.td}>{formatDate(d.archived_at || manifest.created_at)}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}

                                {allVisibleIds.length === 0 && visibleDeployments.length === 0 && (
                                    <tr><td colSpan="5" style={S.emptyRow}>No matching items found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* Preflight review: shows the resolved roots, the dependency closure
                that would be restored, and any BLOCKING ID collisions. With no
                collisions the operator confirms explicitly; with collisions there is
                no confirm path (no overwrite / import-as-new / re-home) — the
                operator clears the conflict out of band and retries. */}
            {restorePreview && (
                <div style={S.modalOverlay} onClick={() => setRestorePreview(null)}>
                    <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                        <h3 style={S.modalTitle}>
                            {restorePreview.collisions.length > 0 ? 'Restore blocked — ID collisions' : 'Confirm restore'}
                        </h3>
                        <p style={S.modalSubtitle}>
                            {restorePreview.collisions.length > 0
                                ? 'These archived IDs already exist in your account. Selective restore never overwrites, merges, or re-homes IDs, so nothing was applied. Remove the conflicting records and retry, or deselect the colliding roots.'
                                : 'Review the roots and dependencies below, then confirm to apply the restore.'}
                        </p>

                        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <h4 style={S.modalSectionTitle}>Selected roots</h4>
                                <div style={{ fontSize: 13, color: '#E2E8F0', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    <span>Surveys: {restorePreview.report?.resolved_roots?.survey_ids?.length || 0}</span>
                                    <span>Deployments: {restorePreview.report?.resolved_roots?.deployment_ids?.length || 0}</span>
                                    <span>Recipients: {restorePreview.report?.resolved_roots?.recipient_ids?.length || 0}</span>
                                </div>
                            </div>

                            <div>
                                <h4 style={S.modalSectionTitle}>Dependencies to restore</h4>
                                {restorePreview.report?.planned_counts && Object.keys(restorePreview.report.planned_counts).length > 0 ? (
                                    <div style={S.archiveList}>
                                        {Object.entries(restorePreview.report.planned_counts)
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([table, count]) => (
                                                <div key={table} style={S.archiveRow}>
                                                    <span style={S.archiveRowName}>{table}</span>
                                                    <span style={S.archiveBadge}>{count}</span>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 13, color: '#B4C0D3', fontStyle: 'italic' }}>No rows resolved.</div>
                                )}
                            </div>

                            {restorePreview.collisions.length > 0 && (
                                <div>
                                    <h4 style={{ ...S.modalSectionTitle, color: '#FDA4AF' }}>Collisions (blocking)</h4>
                                    <div style={S.archiveList}>
                                        {restorePreview.collisions.map((c, idx) => (
                                            <div key={idx} style={{ ...S.archiveRow, flexWrap: 'wrap' }}>
                                                <div style={{ flex: '1 1 100%', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                                                    <span style={S.archiveRowName}>{c.value}</span>
                                                    <span style={S.archiveBadge}>{c.table}</span>
                                                </div>
                                                <div style={{ flex: '1 1 100%', fontSize: 11, color: '#94A3B8' }}>key: {c.key}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ ...S.modalActions, gap: 8 }}>
                            <button
                                type="button"
                                style={S.modalCancelBtn}
                                onClick={() => setRestorePreview(null)}
                                disabled={reloading}
                            >
                                {restorePreview.collisions.length > 0 ? 'Close' : 'Cancel'}
                            </button>
                            {restorePreview.collisions.length === 0 && (
                                <button
                                    type="button"
                                    style={S.modalConfirmBtn}
                                    onClick={confirmRestore}
                                    disabled={reloading}
                                >
                                    {reloading ? 'Restoring...' : 'Confirm restore'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <TextInputModal
                open={!!namePrompt}
                title="New Archive"
                message="Name your new archive file. The .samarchive extension is added automatically."
                label="Archive name"
                initialValue="workspace.samarchive"
                placeholder="workspace.samarchive"
                confirmLabel="Create"
                onSubmit={handleNameSubmit}
                onCancel={() => setNamePrompt(null)}
            />

            <ConfirmModal
                open={!!overwritePrompt}
                title="Overwrite archive?"
                message={overwritePrompt
                    ? `"${overwritePrompt.name}" already exists in this folder. Overwrite it with a new empty archive?`
                    : ''}
                confirmLabel="Overwrite"
                danger
                onConfirm={handleOverwriteConfirm}
                onCancel={() => setOverwritePrompt(null)}
            />

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .spin { animation: spin 1s linear infinite; }
            `}</style>
        </div>
    );
}

const S = {
    container: { padding: '2px 0' },
    header: { marginBottom: 24 },
    title: { 
        margin: 0, fontSize: 20, fontWeight: 600, color: '#F1F5F9', 
        display: 'flex', alignItems: 'center', gap: 10 
    },
    titleIcon: { color: '#5B9FFF' },
    subtitle: { margin: '4px 0 0', fontSize: 13, color: '#C3CDDC' },
    
    card: {
        background: '#1E293B', border: '1px solid #334155',
        borderRadius: 12, padding: 20, marginBottom: 20,
    },
    cardHeader: { 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
        marginBottom: 16, flexWrap: 'wrap', gap: 12
    },
    cardTitle: { margin: 0, fontSize: 15, fontWeight: 600, color: '#E2E8F0' },
    
    fileActions: { display: 'flex', gap: 8 },
    actionBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', background: '#334155', border: '1px solid #475569',
        borderRadius: 6, color: '#E2E8F0', fontSize: 13, fontWeight: 500,
        cursor: 'pointer', transition: 'all 0.15s'
    },
    btnIcon: { color: '#94A3B8' },
    
    fileInfo: { display: 'flex', flexDirection: 'column', gap: 12 },
    activeFileLine: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6
    },
    checkIcon: { color: '#22C55E' },
    fileName: { fontSize: 14, fontWeight: 600, color: '#F1F5F9' },
    filePath: { fontSize: 12, color: '#A3B1C6', fontFamily: 'monospace' },
    
    healthRow: { display: 'flex', alignItems: 'center', gap: 12 },
    healthStrip: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500
    },
    healthText: { whiteSpace: 'nowrap' },
    reopenBtn: {
        background: '#5B9FFF', border: 'none', borderRadius: 4,
        color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 10px', cursor: 'pointer'
    },
    refreshBtn: {
        background: 'transparent', border: 'none', color: '#94A3B8', 
        padding: 4, cursor: 'pointer', display: 'flex'
    },
    
    contentsActions: { display: 'flex', gap: 12, alignItems: 'center', flex: 1, justifyContent: 'flex-end' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: 300 },
    searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748B' },
    searchInput: {
        width: '100%', padding: '7px 32px', background: '#0F172A', 
        border: '1px solid #334155', borderRadius: 6, color: '#F1F5F9',
        fontSize: 13, outline: 'none', boxSizing: 'border-box'
    },
    searchClear: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748B', cursor: 'pointer' },
    
    reloadBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', background: '#5B9FFF', border: 'none',
        borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer'
    },
    
    dataSummary: {
        padding: '8px 14px', marginBottom: 10, borderRadius: 8,
        background: '#0F172A', border: '1px solid #334155',
        fontSize: 13, color: '#C3CDDC'
    },
    dataSummaryEmpty: { color: '#F59E0B' },
    contentsTable: {
        background: '#0F172A', borderRadius: 8, border: '1px solid #334155',
        overflow: 'hidden'
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        textAlign: 'left', padding: '10px 16px', background: '#1E293B',
        fontSize: 12, fontWeight: 700, color: '#A3B1C6', textTransform: 'uppercase',
        letterSpacing: '0.05em', borderBottom: '1px solid #334155'
    },
    groupHeader: {
        background: 'rgba(255,255,255,0.02)', fontSize: 12, fontWeight: 600,
        color: '#C3CDDC', padding: '8px 16px', borderBottom: '1px solid #334155'
    },
    row: { borderBottom: '1px solid #334155' },
    td: { padding: '12px 16px', fontSize: 13, color: '#CBD5E1' },
    checkbox: { cursor: 'pointer', color: '#64748B', display: 'flex' },
    rowDetail: { marginLeft: 8, fontSize: 12, color: '#B4C0D3' },
    typeIcon: { color: '#64748B', verticalAlign: 'middle', marginRight: 4 },
    chip: {
        background: '#334155', padding: '2px 8px', borderRadius: 4,
        fontSize: 11, fontWeight: 500, color: '#CBD5E1'
    },
    mono: { fontFamily: 'monospace', color: '#A3B1C6', fontSize: 12 },
    emptyState: { padding: '40px 0', textAlign: 'center' },
    emptyText: { color: '#B4C0D3', fontSize: 13 },
    emptyRow: { padding: 24, textAlign: 'center', color: '#B4C0D3', fontSize: 13 },

    modalOverlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    },
    modalCard: {
        background: '#1E293B', border: '1px solid #334155', borderRadius: 12,
        padding: 20, width: 'min(520px, 90vw)', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column'
    },
    modalTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: '#F1F5F9' },
    modalSubtitle: { margin: '4px 0 16px', fontSize: 12, color: '#C3CDDC', fontFamily: 'monospace' },
    archiveList: {
        overflowY: 'auto', border: '1px solid #334155', borderRadius: 8,
        background: '#0F172A'
    },
    archiveRow: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderBottom: '1px solid #1E293B'
    },
    archiveRowName: { flex: 1, fontSize: 13, color: '#E2E8F0' },
    archiveBadge: {
        fontSize: 11, fontWeight: 600, padding: '2px 8px',
        borderRadius: 4, background: 'rgba(255,255,255,0.05)'
    },
    modalActions: { display: 'flex', justifyContent: 'flex-end', marginTop: 16 },
    modalCancelBtn: {
        padding: '8px 16px', background: '#334155', border: '1px solid #475569',
        borderRadius: 6, color: '#E2E8F0', fontSize: 13, fontWeight: 500, cursor: 'pointer'
    },
    modalConfirmBtn: {
        padding: '8px 16px', background: '#5B9FFF', border: 'none',
        borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer'
    },
    modalSectionTitle: {
        margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#C3CDDC',
        textTransform: 'uppercase', letterSpacing: '0.05em'
    }
};
